import styles from './Popup.module.scss';

/**
 * Returns the ANCHOR POINT position for the POPUP as defined by an (x,y) grid with values from -1 to 1, with the
 * content screen position at he center (0,0). The point it to keep the popup nicely contained within the container
 * and not spill out over the edge.
 *
 *
 *  (-1, -1) (0, -1) (1, -1)
 *  (-1,  0)         (1,  0)
 *  (-1,  1) (0,  1) (1, -1)
 *
 * For example (-1, -1) means the ANCHOR POINT on the popup is at the top-left corner OF THE POPUP (e.g. assuming
 * the popup is on a route waypoint, the waypoint marker is at the top-left corner of the popup).
 *
 * The DEFAULT position is (0, 1) (e.g. popup is centrally aligned above the content), and will be adopted unless the
 * popup will spill out of the map screen.
 *
 * The central position - which would be (0, 0) - is never returned; this would obscure the content itself.
 */

type Position = 0 | 1 | -1;

export const getPopupAnchorPosition = (
  anchorPointXPixels: number, // distance across from the left of the container, in pixels
  anchorPointYPixels: number, // distance down from the top of the container, in pixels
  containerWidthPixels: number,
  containerHeightPixels: number,
  popupWidthPixels: number,
  popupHeightPixels: number,
  calloutTriangleSizePixels: number
) => {
  // By default, horizontal alignment is the central position (0), unless the popup would spill out the sides
  // of the container otherwise.
  // Note: we don't consider the `calloutTriangleSizePixels` when determining left/right alignment as in the default
  // case, the "callout triangle" will be in a vertical direction and not impact this.
  let horizontal: Position = 0;
  if (anchorPointXPixels + popupWidthPixels / 2 > containerWidthPixels) {
    // If in the central position we would spill of the right of the container, we set the anchor point to the right
    // side of the popup.
    horizontal = 1;
  } else if (anchorPointXPixels - popupWidthPixels / 2 < 0) {
    // If in the central position we would spill of the left of the container, we set the anchor point to the left
    // side of the popup.
    horizontal = -1;
  }

  let vertical: Position = 1;
  if (horizontal === 0) {
    // When centrally aligned horizontally, we will have anchor at the bottom (default, 1), or at the top if the
    // popup would otherwise spill out the top of the container.
    if (anchorPointYPixels < popupHeightPixels + calloutTriangleSizePixels) {
      vertical = -1;
    }
  } else {
    // When NOT centrally aligned horizontally, the vertical position will prefer to be central, BUT can go top or
    // bottom if it would otherwise spill out the container (picture around the corners of the container here).
    if (anchorPointYPixels < popupHeightPixels / 2) {
      vertical = -1;
    } else if (
      anchorPointYPixels >
      containerHeightPixels - popupHeightPixels / 2
    ) {
      vertical = 1;
    } else {
      vertical = 0;
    }
  }

  return {
    horizontal,
    vertical
  };
};

export const getPopupAnchorClassName = (
  horizontal: Position,
  vertical: Position
): string => {
  // empty string should never be returned, but it's a way to avoid using non-null assertion
  return anchorPositionMap[vertical + 1]?.[horizontal + 1] || '';
};

const anchorPositionMap: string[][] = [
  [
    styles.popupAnchorTopLeft,
    styles.popupAnchorTop,
    styles.popupAnchorTopRight
  ],
  [
    styles.popupAnchorLeft,
    styles.popupAnchorBottom, // we do not expect to ever receive (0,0)
    styles.popupAnchorRight
  ],
  [
    styles.popupAnchorBottomLeft,
    styles.popupAnchorBottom,
    styles.popupAnchorBottomRight
  ]
] as string[][]; // casting to avoid typecheck error in CI. Currently we have editor type support for styles, but the build tsconfig infers styles values as `string | undefined`
